import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileText, AlertTriangle, ArrowLeft } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { dayKey, addMonthsToKey } from '../lib/practiceTime';
import { formatMinutes, shortDates } from '../lib/billingSummary';
import { t, formatDate, formatMoney } from '../i18n';
import { Card, Button, Alert, Spinner, EmptyState, Table } from '../components/ui';

/**
 * What a professional has to bill for a month (migration 0053): prestador
 * sessions by obra social and patient, and the patients they invoice.
 *
 * The same summary goes out by email on the first weekday of the next month;
 * here it can be read for any month, and read again after closing a session the
 * summary said was still open. Opens on the month before, which is the one
 * normally being billed.
 */

const count = (sessions, minutes) => (sessions ? `${sessions} · ${formatMinutes(minutes)}` : '—');

const Charged = ({ line }) => (line.charged_sessions ? (
  <>
    {count(line.charged_sessions, line.charged_minutes)} · {formatMoney(line.charged_amount)}
    <p className="text-xs text-slate-500">{shortDates(line.charged_dates)}</p>
  </>
) : '—');

const cell = 'px-3 py-2 align-top';

const BillingSummaryView = () => {
  const [month, setMonth] = useState(() => addMonthsToKey(dayKey().slice(0, 7), -1));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    supabase.rpc('my_billing_summary', { p_month: `${month}-01` }).then(({ data, error: loadError }) => {
      if (!active) return;
      setLoading(false);
      if (loadError) { setError(loadError.message); setSummary(null); return; }
      setSummary(data);
    });
    return () => { active = false; };
  }, [month]);

  const empty = summary && summary.coverages.length === 0 && summary.invoiced.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('billingSummary.title')}</h1>
          <p className="text-sm text-slate-500">{t('billingSummary.subtitle')}</p>
        </div>
        <Link to="/pagos">
          <Button variant="secondary"><ArrowLeft size={16} />{t('nav.payments')}</Button>
        </Link>
      </div>

      <div className="flex items-center justify-between gap-3">
        <Button variant="secondary" onClick={() => setMonth(addMonthsToKey(month, -1))} title={t('billingSummary.previousMonth')}>
          <ChevronLeft size={16} />
        </Button>
        <p className="text-sm font-semibold text-slate-800 first-letter:uppercase">
          {formatDate(`${month}-01`, { month: 'long', year: 'numeric' })}
        </p>
        <Button variant="secondary" onClick={() => setMonth(addMonthsToKey(month, 1))} title={t('billingSummary.nextMonth')}>
          <ChevronRight size={16} />
        </Button>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? <Spinner /> : summary && (
        <>
          {summary.unclosed > 0 && (
            <Alert tone="warning">
              <span className="flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                {t('billingSummary.unclosed', { count: summary.unclosed })}
              </span>
            </Alert>
          )}

          {empty ? (
            <Card><EmptyState icon={FileText} title={t('billingSummary.empty')} /></Card>
          ) : (
            <>
              {summary.coverages.map((coverage) => (
                <Card
                  key={coverage.company}
                  title={coverage.company}
                  subtitle={t('billingSummary.coverageSubtitle', {
                    count: coverage.sessions,
                    hours: formatMinutes(coverage.minutes),
                    amount: formatMoney(coverage.owed_by_plan),
                  })}
                >
                  <Table headers={[
                    t('billingSummary.columns.patient'), t('billingSummary.columns.idNumber'),
                    t('billingSummary.columns.plan'), t('billingSummary.columns.held'),
                    t('billingSummary.columns.dates'), t('billingSummary.columns.byPlan'),
                    t('billingSummary.columns.copays'), t('billingSummary.columns.charged'),
                    t('billingSummary.columns.uncharged'),
                  ]}>
                    {coverage.patients.map((line) => (
                      <tr key={`${line.patient}-${line.plan}`}>
                        <td className={`${cell} font-medium text-slate-900`}>{line.patient}</td>
                        <td className={cell}>{line.id_number || '—'}</td>
                        <td className={cell}>{line.plan || '—'}</td>
                        <td className={`${cell} whitespace-nowrap`}>{count(line.held_sessions, line.held_minutes)}</td>
                        <td className={`${cell} text-slate-600`}>{shortDates(line.held_dates) || '—'}</td>
                        <td className={`${cell} whitespace-nowrap font-medium`}>{formatMoney(line.owed_by_plan)}</td>
                        <td className={`${cell} whitespace-nowrap`}>{formatMoney(line.copays)}</td>
                        <td className={`${cell} whitespace-nowrap`}><Charged line={line} /></td>
                        <td className={`${cell} whitespace-nowrap`}>{count(line.uncharged_sessions, line.uncharged_minutes)}</td>
                      </tr>
                    ))}
                  </Table>
                </Card>
              ))}

              {summary.invoiced.length > 0 && (
                <Card title={t('billingSummary.invoicedTitle')} subtitle={t('billingSummary.invoicedSubtitle')}>
                  <Table headers={[
                    t('billingSummary.columns.patient'), t('billingSummary.columns.idNumber'),
                    t('billingSummary.columns.coverage'), t('billingSummary.columns.held'),
                    t('billingSummary.columns.dates'), t('billingSummary.columns.heldAmount'),
                    t('billingSummary.columns.charged'), t('billingSummary.columns.uncharged'),
                    t('billingSummary.columns.toInvoice'),
                  ]}>
                    {summary.invoiced.map((line) => (
                      <tr key={line.patient}>
                        <td className={`${cell} font-medium text-slate-900`}>{line.patient}</td>
                        <td className={cell}>{line.id_number || '—'}</td>
                        <td className={cell}>{line.coverage}</td>
                        <td className={`${cell} whitespace-nowrap`}>{count(line.held_sessions, line.held_minutes)}</td>
                        <td className={`${cell} text-slate-600`}>{shortDates(line.held_dates) || '—'}</td>
                        <td className={`${cell} whitespace-nowrap`}>{formatMoney(line.held_amount)}</td>
                        <td className={`${cell} whitespace-nowrap`}><Charged line={line} /></td>
                        <td className={`${cell} whitespace-nowrap`}>{count(line.uncharged_sessions, line.uncharged_minutes)}</td>
                        <td className={`${cell} whitespace-nowrap font-semibold`}>{formatMoney(line.to_invoice)}</td>
                      </tr>
                    ))}
                  </Table>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default BillingSummaryView;
